/**
 * Admin Adoptions Tab - Manage adoption applications
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, CheckCircle, X, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { useAdmin } from "./AdminContext";
import { API } from "../../lib/api";

const AdminAdoptionsTab = () => {
  const { adoptionApplications, loadData } = useAdmin();
  
  const [applicationFilter, setApplicationFilter] = useState("all");
  const [viewingApplication, setViewingApplication] = useState(null);

  const filteredApplications = adoptionApplications.filter(a => {
    if (applicationFilter === "pending") return a.status === "pending";
    if (applicationFilter === "approved") return a.status === "approved";
    if (applicationFilter === "rejected") return a.status === "rejected";
    return true;
  });

  const handleUpdateApplicationStatus = async (applicationId, status) => {
    try {
      await axios.put(`${API}/adoptions/${applicationId}/status`, { status }, { withCredentials: true });
      toast.success(`Application ${status}`);
      loadData();
      setViewingApplication(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update application");
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    if (!window.confirm("Are you sure you want to delete this application?")) return;
    try {
      await axios.delete(`${API}/adoptions/${applicationId}`, { withCredentials: true });
      toast.success("Application deleted");
      loadData();
    } catch {
      toast.error("Failed to delete application");
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Adoption Applications</CardTitle>
          <CardDescription>Review and manage adoption applications from potential adopters</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {["all", "pending", "approved", "rejected"].map(filter => (
              <Button
                key={filter}
                variant={applicationFilter === filter ? "default" : "outline"}
                size="sm"
                onClick={() => setApplicationFilter(filter)}
                className={applicationFilter === filter ? "bg-amber-600 hover:bg-amber-700" : ""}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)} 
                ({filter === "all" ? adoptionApplications.length : adoptionApplications.filter(a => a.status === filter).length})
              </Button>
            ))}
          </div>
          
          {filteredApplications.length === 0 ? (
            <p className="text-stone-500 text-center py-8">No adoption applications found</p>
          ) : (
            <div className="space-y-4">
              {filteredApplications.map(app => (
                <Card key={app.application_id} className={`border-l-4 ${app.status === "pending" ? "border-l-amber-500" : app.status === "approved" ? "border-l-green-500" : "border-l-red-500"}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-stone-900">{app.applicant_names}</h4>
                          <Badge className={app.status === "pending" ? "bg-amber-100 text-amber-700" : app.status === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                            {app.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-stone-600 mb-1">
                          <strong>Cat:</strong> {app.cat_name || "General Interest"} | <strong>Email:</strong> {app.email} | <strong>Phone:</strong> {app.phone}
                        </p>
                        <p className="text-xs text-stone-500">
                          Applied: {new Date(app.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => setViewingApplication(app)}>
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        {app.status === "pending" && (
                          <>
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleUpdateApplicationStatus(app.application_id, "approved")}>
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleUpdateApplicationStatus(app.application_id, "rejected")}>
                              <X className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteApplication(app.application_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Application Dialog */}
      <Dialog open={!!viewingApplication} onOpenChange={() => setViewingApplication(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewingApplication && (
            <>
              <DialogHeader>
                <DialogTitle>Application for {viewingApplication.cat_name || "General Interest"}</DialogTitle>
                <DialogDescription>
                  Application ID: {viewingApplication.application_id}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-stone-500">Applicant Name(s)</Label>
                    <p className="font-medium">{viewingApplication.applicant_names}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">Status</Label>
                    <Badge className={viewingApplication.status === "pending" ? "bg-amber-100 text-amber-700" : viewingApplication.status === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                      {viewingApplication.status}
                    </Badge>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-stone-500">Email</Label>
                    <p>{viewingApplication.email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">Phone</Label>
                    <p>{viewingApplication.phone}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Address</Label>
                  <p>{viewingApplication.address}</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-stone-500">Property Size</Label>
                    <p>{viewingApplication.property_size}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">Indoor/Outdoor Plan</Label>
                    <p>{viewingApplication.indoor_outdoor}</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-stone-500">Other Pets</Label>
                    <p>{viewingApplication.has_other_pets ? `Yes - ${viewingApplication.other_pets_details}` : "No"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">Children</Label>
                    <p>{viewingApplication.has_children ? `Yes - Ages: ${viewingApplication.children_details}` : "No"}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Previous Experience with Cats</Label>
                  <p className="bg-stone-50 p-3 rounded-lg text-sm">{viewingApplication.previous_experience}</p>
                </div>
                {viewingApplication.additional_info && (
                  <div>
                    <Label className="text-xs text-stone-500">Additional Information</Label>
                    <p className="bg-stone-50 p-3 rounded-lg text-sm">{viewingApplication.additional_info}</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                {viewingApplication.status === "pending" && (
                  <>
                    <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleUpdateApplicationStatus(viewingApplication.application_id, "approved")}>
                      <CheckCircle className="w-4 h-4 mr-2" /> Approve
                    </Button>
                    <Button variant="destructive" onClick={() => handleUpdateApplicationStatus(viewingApplication.application_id, "rejected")}>
                      <X className="w-4 h-4 mr-2" /> Reject
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => setViewingApplication(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminAdoptionsTab;
